import { apiClient } from "@base/api/client";
import { useIsDocumentForeground } from "@base/model/hooks";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import type { AIRefineResult, AIRefineTaskResponse } from "./schema";

const AI_REFINE_POLL_INTERVAL_MS = 2000;
const AI_REFINE_POLL_TIMEOUT_MS = 10000;

async function startAIRefine(projectId: string, description: string): Promise<AIRefineTaskResponse> {
  const response = await apiClient.post<AIRefineTaskResponse>(
    `/project-management/api/projects/${projectId}/daily-logs/ai`,
    {
      description,
    },
  );
  return response.data;
}

async function fetchAIRefineResult(projectId: string, taskId: string): Promise<AIRefineResult> {
  const response = await apiClient.get<AIRefineResult>(
    `/project-management/api/projects/${projectId}/daily-logs/ai/${taskId}`,
  );
  return response.data;
}

export function useAIRefine(projectId: string) {
  const [taskId, setTaskId] = useState<string | null>(null);
  const [isTimedOut, setIsTimedOut] = useState(false);
  const isDocumentForeground = useIsDocumentForeground();
  const queryClient = useQueryClient();

  useEffect(() => {
    return () => {
      queryClient.cancelQueries({ queryKey: ["ai-refine", projectId] });
    };
  }, [queryClient, projectId]);

  useEffect(() => {
    if (!taskId) return;

    const timer = setTimeout(() => setIsTimedOut(true), AI_REFINE_POLL_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [taskId]);

  const startMutation = useMutation({
    mutationFn: (data: {
      description: string;
    }) => startAIRefine(projectId, data.description),
    onSuccess: (data) => {
      setTaskId(data.task_id);
      setIsTimedOut(false);
    },
    onError: () => {
      toast.error("Failed to start AI refinement. Please try again.");
    },
  });

  const resultQuery = useQuery({
    queryKey: ["ai-refine", projectId, taskId],
    queryFn: () => {
      if (!taskId) throw new Error("Task ID is required");

      return fetchAIRefineResult(projectId, taskId);
    },
    enabled: !!projectId && !!taskId && isDocumentForeground && !isTimedOut,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === "SUCCESS" || status === "FAILURE") return false;
      return AI_REFINE_POLL_INTERVAL_MS;
    },
  });

  const isSuccess = resultQuery.data?.status === "SUCCESS";
  const isPending = startMutation.isPending;
  // 작업 시작 요청 중이거나 첫 조회 응답 전에도 진행 중으로 본다. 그래야 누르는 즉시 로딩이 보이고 중복 요청을 막는다.
  const isAwaitingResult =
    !!taskId && !isTimedOut && resultQuery.data?.status !== "SUCCESS" && resultQuery.data?.status !== "FAILURE";
  const isRefining = startMutation.isPending || isAwaitingResult;
  const isFailure = resultQuery.data?.status === "FAILURE" || (isTimedOut && !isSuccess);

  const refine = (description: string) => {
    if (!projectId) {
      toast.error("Project ID is missing. Cannot refine with AI.");
      return;
    }
    setTaskId(null);
    setIsTimedOut(false);
    startMutation.mutate({ description });
  };

  const reset = () => {
    setTaskId(null);
    setIsTimedOut(false);
  };

  return {
    refine,
    reset,
    isPending,
    isRefining,
    isSuccess,
    isFailure,
    description: isSuccess ? resultQuery.data?.description : undefined,
  };
}
